'use client';

import { useCallback } from 'react';

interface SkipLinkProps {
  targetId?: string;
  label?: string;
}

/**
 * Componente SkipLink para permitir que usuários de teclado e screen readers
 * pulem diretamente para o conteúdo principal
 */
export function SkipLink({ 
  targetId = 'main-content', 
  label = 'Pular para o conteúdo principal' 
}: SkipLinkProps) {
  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
      // Remover tabindex após o foco para não afetar navegação normal
      setTimeout(() => target.removeAttribute('tabindex'), 100);
    }
  }, [targetId]);

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      {label}
    </a>
  );
}

/**
 * Componente VisuallyHidden para esconder conteúdo visualmente
 * mas mantê-lo acessível para screen readers
 */
export function VisuallyHidden({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
      style={{ clip: 'rect(0, 0, 0, 0)' }}
    >
      {children}
    </span>
  );
}

/**
 * Componente LiveRegion para anúncios dinâmicos para screen readers
 */
interface LiveRegionProps {
  children: React.ReactNode;
  'aria-live'?: 'polite' | 'assertive' | 'off';
  'aria-atomic'?: boolean;
  role?: 'status' | 'alert' | 'log';
}

export function LiveRegion({
  children,
  'aria-live': ariaLive = 'polite',
  'aria-atomic': ariaAtomic = true,
  role = 'status',
}: LiveRegionProps) {
  return (
    <div
      role={role}
      aria-live={ariaLive}
      aria-atomic={ariaAtomic}
      className="sr-only"
    >
      {children}
    </div>
  );
}
